import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import { useStore } from '../store';
import { LANGS } from '../i18n';
import './Login.css';

const LOGIN = gql`
  mutation Login($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      token
      user { id username role partnerId siteId }
    }
  }
`;

const AUDIT_URL = 'https://audit.dronedefender.online/';
// Раздел «Тренинг» — в портале аудита (П8): миссии на консоли A3O Mini Rapid, история проходов у человека
const TRAINING_URL = 'https://audit.dronedefender.online/training';
// Выбор сервиса (Audit / Defense / Training) — только на собственном домене DroneDefender.
// На доменах партнёров (white-label) — прежняя форма входа без плиток.
const SERVICE_HOSTS = ['dronedefender.online', 'www.dronedefender.online', 'localhost', '127.0.0.1'];

const Icon = {
  audit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" /><path d="M16 16l5 5" /><path d="M8.5 11h5M11 8.5v5" />
    </svg>
  ),
  defense: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l7 3v5c0 4.5-3 8.2-7 10-4-1.8-7-5.5-7-10V6z" /><path d="M9 12l2 2 4-4" />
    </svg>
  ),
  training: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l9-4 9 4-9 4z" /><path d="M7 11v4.5c0 1.4 2.2 2.5 5 2.5s5-1.1 5-2.5V11" /><path d="M21 9v5" />
    </svg>
  ),
  external: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  ),
  chevron: (
    <svg className="svc-flip" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 5l7 7-7 7" />
    </svg>
  ),
  back: (
    <svg className="svc-flip" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" />
    </svg>
  ),
};

export default function Login() {
  const { t, i18n } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const setAuth = useStore(s => s.setAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const [login, { loading }] = useMutation(LOGIN);
  const userRef = useRef<HTMLInputElement>(null);

  const withServices = SERVICE_HOSTS.includes(window.location.hostname);
  const from = (location.state as { from?: string } | null)?.from;
  // Форма Defense открыта: по #defense (кнопка плитки, работает «Назад» браузера)
  // или когда пришли со страницы пульта без сессии (from).
  const defenseOpen = !withServices || location.hash === '#defense' || !!from;

  const openDefense = () => navigate({ hash: 'defense' });
  const backToServices = () => { setError(''); navigate('/login', { replace: true }); };

  useEffect(() => {
    if (withServices && defenseOpen) userRef.current?.focus();
  }, [withServices, defenseOpen]);

  useEffect(() => {
    if (!withServices || !defenseOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') backToServices(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [withServices, defenseOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const { data } = await login({ variables: { username, password } });
      setAuth(data.login.user, data.login.token);
      navigate(from || '/');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    }
  };

  const lang = i18n.resolvedLanguage || 'en';

  const form = (
    <form onSubmit={handleSubmit} className="login-form">
      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      <div className="login-field">
        <label htmlFor="login-username">{t('login.username')}</label>
        <div className="login-input-wrap">
          <svg className="login-input-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-5 0-9 2.5-9 6v2h18v-2c0-3.5-4-6-9-6Z" />
          </svg>
          <input
            id="login-username"
            ref={userRef}
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder={t('login.username')}
            autoComplete="username"
            required
          />
        </div>
      </div>

      <div className="login-field">
        <label htmlFor="login-password">{t('login.password')}</label>
        <div className="login-input-wrap">
          <svg className="login-input-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Zm3 8H9V6a3 3 0 0 1 6 0v3Z" />
          </svg>
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={t('login.password')}
            autoComplete="current-password"
            required
          />
          <button
            type="button"
            className="login-eye"
            onClick={() => setShowPassword(s => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 5.3 3.3 4 20 20.7 18.7 22l-2.9-2.9A11 11 0 0 1 12 20C6 20 2 12 2 12a18 18 0 0 1 4.1-5.1L2 5.3Zm7.6 7.6 2.9 2.9a3 3 0 0 1-2.9-2.9ZM12 7a5 5 0 0 1 5 5c0 .5-.1 1-.2 1.4l3 3A18 18 0 0 0 22 12s-4-8-10-8a11 11 0 0 0-2.8.4l2.2 2.2A5 5 0 0 1 12 7Z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 5c6 0 10 7 10 7s-4 7-10 7S2 12 2 12s4-7 10-7Zm0 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <button type="submit" className="login-btn" disabled={loading}>
        {loading ? t('login.signingIn') : t('login.signIn')}
      </button>
    </form>
  );

  const foot = (
    <div className="login-foot">
      <span className="login-status-dot" /> DroneDefender · v3.1
    </div>
  );

  return (
    <div className={`login-bg${withServices ? ' with-services' : ''}`} dir={i18n.dir(lang)} lang={lang}>
      <div className="login-bg-grid" aria-hidden="true" />
      <div className="login-bg-glow" aria-hidden="true" />

      <select
        value={i18n.resolvedLanguage}
        onChange={e => i18n.changeLanguage(e.target.value)}
        title={t('common.language')}
        className="login-lang"
      >
        {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>

      <div className="login-stage">
        <div className="login-emblem">
          <img
            className="login-shield"
            src={`${process.env.PUBLIC_URL}/login-shield.png`}
            alt="Integrated Drone Defense emblem"
          />
        </div>

        <div className="login-right">
          <img
            className="login-wordmark"
            src={`${process.env.PUBLIC_URL}/login-logo-2026.webp`}
            alt="Integrated Drone Defense — RF Security Platform"
          />

          {withServices ? (
            // Обе панели лежат в одной ячейке: высота панели = большей из двух,
            // поэтому при переходе к форме правая колонка (и щит рядом) не меняет размер.
            <div className="login-panel login-panel-stack">
              <div className={`login-pane${defenseOpen ? ' is-hidden' : ''}`} aria-hidden={defenseOpen}>
                <div className="svc-cap">{t('start.choose')}</div>
                <div className="svc-list">
                  <a className="svc svc-audit" href={AUDIT_URL} tabIndex={defenseOpen ? -1 : undefined}>
                    <span className="svc-icon">{Icon.audit}</span>
                    <span className="svc-body">
                      <span className="svc-head">
                        <span className="svc-title">{t('start.audit')}</span>
                        <span className="svc-chip"><span className="chip-txt">{t('start.open')}</span>{Icon.external}</span>
                      </span>
                      <span className="svc-desc">{t('start.auditDesc')}</span>
                      <span className="svc-url">audit.dronedefender.online</span>
                    </span>
                  </a>
                  <button type="button" className="svc svc-defense" onClick={openDefense} tabIndex={defenseOpen ? -1 : undefined}>
                    <span className="svc-icon">{Icon.defense}</span>
                    <span className="svc-body">
                      <span className="svc-head">
                        <span className="svc-title">{t('start.defense')}</span>
                        <span className="svc-chip"><span className="chip-txt">{t('start.enter')}</span>{Icon.chevron}</span>
                      </span>
                      <span className="svc-desc">{t('start.defenseDesc')}</span>
                    </span>
                  </button>
                  <a className="svc svc-train" href={TRAINING_URL} tabIndex={defenseOpen ? -1 : undefined}>
                    <span className="svc-icon">{Icon.training}</span>
                    <span className="svc-body">
                      <span className="svc-head">
                        <span className="svc-title">{t('start.training')}</span>
                        <span className="svc-chip"><span className="chip-txt">{t('start.open')}</span>{Icon.external}</span>
                      </span>
                      <span className="svc-desc">{t('start.trainingDesc')}</span>
                      <span className="svc-url">audit.dronedefender.online/training</span>
                    </span>
                  </a>
                </div>
                {foot}
              </div>

              <div className={`login-pane login-pane-form${defenseOpen ? '' : ' is-hidden'}`} aria-hidden={!defenseOpen}>
                <button type="button" className="svc-back" onClick={backToServices} tabIndex={defenseOpen ? undefined : -1}>
                  {Icon.back}{t('start.back')}
                </button>
                <div className="svc-console">{Icon.defense}<span>{t('start.console')}</span></div>
                {form}
                {foot}
              </div>
            </div>
          ) : (
            <div className="login-panel">
              {form}
              {foot}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
